import React, { useEffect, useState, useCallback } from "react";
import BarraFiltros, { CampoBusqueda, SinResultados } from "./BarraFiltros";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Grid,
    CircularProgress,
    TableContainer,
    TablePagination,
    styled
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import axios from "axios";

import ModalDepartamento from "./ModalDepartamento";
import ModalActivo from "./ModalActivo";
import ModalTag from "./ModalTag";

const API = "https://api.hmsaa.com.mx:5001/api";

/* ==================== STYLES ==================== */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
    textAlign: "center"
}));

const SectionPaper = styled(Paper)(() => ({
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12
}));

const contarTexto = (mostrados, total, activo) =>
    activo && mostrados !== total
        ? `${mostrados} de ${total}`
        : `${total} ${total === 1 ? "registro" : "registros"}`;

export default function TagsPage() {
    /* ==================== STATES ==================== */
    const [departamentos, setDepartamentos] = useState([]);
    const [activos, setActivos] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchDep, setSearchDep] = useState("");
    const [searchAct, setSearchAct] = useState("");
    const [searchTag, setSearchTag] = useState("");

    const [filterDep, setFilterDep] = useState("");
    const [estadoTag, setEstadoTag] = useState(""); // "" | disponibles | asignados

    const [openDep, setOpenDep] = useState(false);
    const [openActivo, setOpenActivo] = useState(false);
    const [openTag, setOpenTag] = useState(false);

    const [pageDep, setPageDep] = useState(0);
    const [pageAct, setPageAct] = useState(0);
    const [pageTag, setPageTag] = useState(0);

    const [editDep, setEditDep] = useState(null);
    const [editActivo, setEditActivo] = useState(null);

    /*  PAGINACIÓN MÍNIMA */
    const rowsPerPage = 7;

    /* ==================== FETCH ==================== */
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [resDep, resAct, resTag] = await Promise.all([
                axios.get(`${API}/departamentos`),
                axios.get(`${API}/activos`),
                axios.get(`${API}/tags`)
            ]);

            const deps = resDep.data || [];
            setDepartamentos(deps);

            const acts = (resAct.data || []).map(a => {
                const dep = deps.find(d => d.id_departamento === a.id_departamento);
                return {
                    ...a,
                    departamento_abrev: dep?.abreviatura || ""
                };
            });

            setActivos(acts);
            setTags(resTag.data || []);
        } catch {
            Swal.fire("Error", "No se pudieron cargar los datos", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    /* ==================== FILTERS ==================== */

    // Departamentos (solo texto)
    const depFil = departamentos.filter(d =>
        d.nombre.toLowerCase().includes(searchDep.trim().toLowerCase())
    );

    // Activos (texto + departamento)
    const actFil = activos.filter(a => {
        const matchText =
            a.numero.toLowerCase().includes(searchAct.trim().toLowerCase());

        const matchDep =
            !filterDep || String(a.id_departamento) === String(filterDep);

        return matchText && matchDep;
    });

    // Tags (texto + departamento por activo)
    const tagFil = tags.filter(t => {
        const matchText =
            t.codigo.toLowerCase().includes(searchTag.trim().toLowerCase());

        const matchDep =
            !filterDep || String(t.id_departamento) === String(filterDep);

        const matchEstado =
            estadoTag === "" || (estadoTag === "asignados") === Boolean(t.asignado);

        return matchText && matchDep && matchEstado;
    });

    const cambiarDepartamento = (valor) => {
        setFilterDep(valor);
        setPageAct(0);
        setPageTag(0);
    };

    const cambiarEstadoTag = (valor) => {
        setEstadoTag(valor);
        setPageTag(0);
    };

    const limpiarFiltros = () => {
        setSearchDep("");
        setSearchAct("");
        setSearchTag("");
        setFilterDep("");
        setEstadoTag("");
        setPageDep(0);
        setPageAct(0);
        setPageTag(0);
    };

    const hayBusquedas =
        searchDep.trim() !== "" || searchAct.trim() !== "" || searchTag.trim() !== "";

    /* ==================== DELETE ==================== */
    const eliminar = async (endpoint, id, label) => {
        const r = await Swal.fire({
            title: `¿Eliminar ${label}?`,
            icon: "warning",
            showCancelButton: true
        });
        if (!r.isConfirmed) return;

        try {
            await axios.delete(`${API}/${endpoint}/${id}`);
            fetchAll();
        } catch (e) {
            Swal.fire("Error", e.response?.data?.error || "Error", "error");
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={6}>
                <CircularProgress />
                <Typography>Cargando…</Typography>
            </Box>
        );
    }

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6">
                        Gestión de Departamentos, Activos y Tags
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* ===== BARRA DE FILTROS GLOBAL ===== */}
            <BarraFiltros
                filtros={[
                    {
                        id: "departamento",
                        tipo: "select",
                        etiqueta: "Departamento / Tipo",
                        textoTodos: "Todos los departamentos",
                        valor: filterDep,
                        onChange: cambiarDepartamento,
                        opciones: departamentos.map(d => ({
                            valor: d.id_departamento,
                            etiqueta: `${d.abreviatura} — ${d.nombre}`
                        }))
                    },
                    {
                        id: "estado-tag",
                        tipo: "chips",
                        etiqueta: "Tags",
                        valor: estadoTag,
                        onChange: cambiarEstadoTag,
                        opciones: [
                            { valor: "", etiqueta: "Todos" },
                            { valor: "disponibles", etiqueta: "Disponibles" },
                            { valor: "asignados", etiqueta: "Asignados" }
                        ]
                    }
                ]}
                activoExtra={hayBusquedas}
                contador={`${depFil.length} departamentos · ${actFil.length} activos · ${tagFil.length} tags`}
                onLimpiar={limpiarFiltros}
            />


            {/* ===== CONTENEDOR PRINCIPAL ===== */}
            <Box
                sx={{
                    p: 3,
                    height: "calc(100vh - 112px)",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center"
                }}
            >
                <Grid
                    container
                    spacing={3}
                    justifyContent="center"
                    alignItems="stretch"
                    sx={{ width: "100%", maxWidth: 1600, height: "100%" }}
                >
                    {/* ================= DEPARTAMENTOS ================= */}
                    <Grid item xs={12} md={4} display="flex">
                        <SectionPaper>
                            <Box px={2} py={1.5} display="flex" justifyContent="space-between">
                                <Typography variant="h6">Departamentos</Typography>
                                <Button variant="contained" onClick={() => setOpenDep(true)}>
                                    Nuevo
                                </Button>
                            </Box>

                            <Box px={2} pb={1}>
                                <CampoBusqueda
                                    valor={searchDep}
                                    onChange={(v) => { setSearchDep(v); setPageDep(0); }}
                                    placeholder="Buscar departamento"
                                    sx={{ width: '100%' }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1 }}>
                                    {contarTexto(depFil.length, departamentos.length, searchDep.trim() !== "")}
                                </Typography>
                            </Box>

                            <TableContainer sx={{ flexGrow: 1 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Nombre</StyledTableCell>
                                            <StyledTableCell>Abrev.</StyledTableCell>
                                            <StyledTableCell>Acciones</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {depFil
                                            .slice(pageDep * rowsPerPage, pageDep * rowsPerPage + rowsPerPage)
                                            .map(d => (
                                                <TableRow key={d.id_departamento}>
                                                    <TableCell align="center">{d.nombre}</TableCell>
                                                    <TableCell align="center">{d.abreviatura}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setEditDep(d.id_departamento);
                                                                setOpenDep(true);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                eliminar("departamentos", d.id_departamento, "departamento")
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {depFil.length === 0 && (
                                            <SinResultados
                                                colSpan={3}
                                                total={departamentos.length}
                                                onLimpiar={limpiarFiltros}
                                                entidad="departamentos"
                                            />
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={depFil.length}
                                page={pageDep}
                                onPageChange={(_, p) => setPageDep(p)}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[]}
                            />
                        </SectionPaper>
                    </Grid>

                    {/* ================= ACTIVOS ================= */}
                    <Grid item xs={12} md={4} display="flex">
                        <SectionPaper>
                            <Box px={2} py={1.5} display="flex" justifyContent="space-between">
                                <Typography variant="h6">Activos</Typography>
                                <Button variant="contained" onClick={() => setOpenActivo(true)}>
                                    Nuevo
                                </Button>
                            </Box>

                            <Box px={2} pb={1}>
                                <CampoBusqueda
                                    valor={searchAct}
                                    onChange={(v) => { setSearchAct(v); setPageAct(0); }}
                                    placeholder="Buscar número de activo"
                                    sx={{ width: '100%' }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1 }}>
                                    {contarTexto(actFil.length, activos.length, searchAct.trim() !== "" || filterDep !== "")}
                                </Typography>
                            </Box>

                            <TableContainer sx={{ flexGrow: 1 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Número</StyledTableCell>
                                            <StyledTableCell>Depto</StyledTableCell>
                                            <StyledTableCell>Acciones</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {actFil
                                            .slice(pageAct * rowsPerPage, pageAct * rowsPerPage + rowsPerPage)
                                            .map(a => (
                                                <TableRow key={a.id_activo}>
                                                    <TableCell align="center">{a.numero}</TableCell>
                                                    <TableCell align="center">{a.departamento_abrev}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setEditActivo(a.id_activo);
                                                                setOpenActivo(true);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                eliminar("activos", a.id_activo, "activo")
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {actFil.length === 0 && (
                                            <SinResultados
                                                colSpan={3}
                                                total={activos.length}
                                                onLimpiar={limpiarFiltros}
                                                entidad="activos"
                                            />
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={actFil.length}
                                page={pageAct}
                                onPageChange={(_, p) => setPageAct(p)}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[]}
                            />
                        </SectionPaper>
                    </Grid>

                    {/* ================= TAGS ================= */}
                    <Grid item xs={12} md={4} display="flex">
                        <SectionPaper>
                            <Box px={2} py={1.5} display="flex" justifyContent="space-between">
                                <Typography variant="h6">Tags</Typography>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => setOpenTag(true)}
                                >
                                    Nuevo
                                </Button>
                            </Box>

                            <Box px={2} pb={1}>
                                <CampoBusqueda
                                    valor={searchTag}
                                    onChange={(v) => { setSearchTag(v); setPageTag(0); }}
                                    placeholder="Buscar código de tag"
                                    sx={{ width: '100%' }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1 }}>
                                    {contarTexto(tagFil.length, tags.length, searchTag.trim() !== "" || filterDep !== "" || estadoTag !== "")}
                                </Typography>
                            </Box>

                            <TableContainer sx={{ flexGrow: 1 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Código</StyledTableCell>
                                            <StyledTableCell>Acciones</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tagFil
                                            .slice(pageTag * rowsPerPage, pageTag * rowsPerPage + rowsPerPage)
                                            .map(t => (
                                                <TableRow key={t.id_tag}>
                                                    <TableCell align="center">
                                                        <strong>{t.codigo}</strong>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => eliminar("tags", t.id_tag, "tag")}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {tagFil.length === 0 && (
                                            <SinResultados
                                                colSpan={2}
                                                total={tags.length}
                                                onLimpiar={limpiarFiltros}
                                                entidad="tags"
                                            />
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={tagFil.length}
                                page={pageTag}
                                onPageChange={(_, p) => setPageTag(p)}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[]}
                            />
                        </SectionPaper>
                    </Grid>
                </Grid>
            </Box>

            {/* ================= MODALES ================= */}
            <ModalDepartamento
                open={openDep}
                onClose={() => {
                    setOpenDep(false);
                    setEditDep(null);
                }}
                editing={editDep}
                refresh={fetchAll}
            />

            <ModalActivo
                open={openActivo}
                onClose={() => {
                    setOpenActivo(false);
                    setEditActivo(null);
                }}
                editing={editActivo}
                departamentos={departamentos}
                refresh={fetchAll}
            />

            <ModalTag
                open={openTag}
                onClose={() => setOpenTag(false)}
                departamentos={departamentos}
                activos={activos}
                refresh={fetchAll}
            />

        </>
    );
}
